import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {
  ActivityIndicator,
  Image,
  LayoutChangeEvent,
  Pressable,
  StyleSheet,
  Text,
  View,
} from 'react-native';
import Carousel, {ICarouselInstance} from 'react-native-reanimated-carousel';
import Svg, {Path} from 'react-native-svg';

import {useWebScreenDimensions} from '../../hooks/useWebScreenDimensions';
import theme from '../../style/theme';

import {
  BANNER_ASPECT_RATIO_MOBILE,
  BANNER_ASPECT_RATIO_WEB,
  BANNER_DESKTOP_MAX_HEIGHT,
  BANNER_DESKTOP_MAX_WIDTH,
  CAROUSEL_ARROW_SIZE,
  CAROUSEL_BORDER_RADIUS,
  CAROUSEL_DOTS,
  CAROUSEL_PEEK_RATIO,
  CAROUSEL_SLIDE_GAP,
} from './constants';
import {CarouselSlide, getSlideImageUrl, HomepageCarouselProps} from './types';

const DEFAULTS = {
  autoPlay: true,
  autoPlayInterval: 5000,
  loop: true,
  pauseOnInteraction: true,
  resumeDelay: 3000,
  transitionDuration: 300,
  showIndicators: true,
  showArrows: true,
};

const HomepageCarouselWeb: React.FC<HomepageCarouselProps> = ({
  slides,
  isLoading,
  onSlidePress,
  testID = 'homepage-carousel',
  peekNextSlide = false,
}) => {
  const {
    autoPlay,
    autoPlayInterval,
    loop,
    pauseOnInteraction,
    resumeDelay,
    transitionDuration,
    showIndicators,
    showArrows: showArrowsConfig,
  } = DEFAULTS;
  const {isWebDesktopScreen} = useWebScreenDimensions();
  const carouselRef = useRef<ICarouselInstance>(null);
  const [activeIndex, setActiveIndex] = useState(0);
  const [isAutoPlay, setIsAutoPlay] = useState(true);
  const [isHovered, setIsHovered] = useState(false);
  const [containerWidth, setContainerWidth] = useState(0);
  const resumeTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const isSingleSlide = slides.length <= 1;

  useEffect(() => {
    return () => {
      if (resumeTimeoutRef.current) {
        clearTimeout(resumeTimeoutRef.current);
      }
    };
  }, []);

  const containerHeight = useMemo(() => {
    if (!containerWidth) return 0;
    const aspectRatio = isWebDesktopScreen ? BANNER_ASPECT_RATIO_WEB : BANNER_ASPECT_RATIO_MOBILE;
    const height = containerWidth / aspectRatio;
    return isWebDesktopScreen ? Math.min(height, BANNER_DESKTOP_MAX_HEIGHT) : height;
  }, [containerWidth, isWebDesktopScreen]);

  const carouselWidth = containerWidth ? containerWidth + CAROUSEL_SLIDE_GAP : 0;

  const itemWidth = useMemo(() => {
    if (!containerWidth) return 0;
    return isSingleSlide || !peekNextSlide ? carouselWidth : carouselWidth * CAROUSEL_PEEK_RATIO;
  }, [containerWidth, carouselWidth, isSingleSlide, peekNextSlide]);

  const handleLayout = useCallback((event: LayoutChangeEvent) => {
    const {width} = event.nativeEvent.layout;
    setContainerWidth(prev => (prev === width ? prev : width));
  }, []);

  const handleSlidePress = useCallback(
    (slide: CarouselSlide) => {
      if (onSlidePress && slide.pageLink) {
        onSlidePress(slide.pageLink);
      }
    },
    [onSlidePress],
  );

  const handleMouseEnter = useCallback(() => {
    setIsHovered(true);
    if (resumeTimeoutRef.current) {
      clearTimeout(resumeTimeoutRef.current);
    }
    if (pauseOnInteraction) {
      setIsAutoPlay(false);
    }
  }, [pauseOnInteraction]);

  const handleMouseLeave = useCallback(() => {
    setIsHovered(false);
    if (pauseOnInteraction) {
      resumeTimeoutRef.current = setTimeout(() => {
        setIsAutoPlay(true);
      }, resumeDelay);
    }
  }, [pauseOnInteraction, resumeDelay]);

  const handlePrev = useCallback(() => {
    if (loop && activeIndex === 0) {
      carouselRef.current?.scrollTo({index: slides.length - 1, animated: true});
    } else {
      carouselRef.current?.prev({animated: true});
    }
  }, [loop, activeIndex, slides.length]);

  const handleNext = useCallback(() => {
    if (loop && activeIndex === slides.length - 1) {
      carouselRef.current?.scrollTo({index: 0, animated: true});
    } else {
      carouselRef.current?.next({animated: true});
    }
  }, [loop, activeIndex, slides.length]);

  const handleSnapToItem = useCallback(
    (index: number) => {
      setActiveIndex(index);
      if (index === slides.length - 1 && autoPlay) {
        resumeTimeoutRef.current = setTimeout(() => {
          carouselRef.current?.scrollTo({index: 0, animated: true});
        }, autoPlayInterval);
      }
    },
    [slides.length, autoPlay, autoPlayInterval],
  );

  const renderSlide = useCallback(
    ({item, index}: {item: CarouselSlide; index: number}) => {
      const imageUrl = getSlideImageUrl(item, isWebDesktopScreen);

      return (
        <Pressable
          testID={`carousel-slide-${index}`}
          onPress={() => handleSlidePress(item)}
          accessibilityRole="button"
          accessibilityLabel={item.content}
          style={styles.slideGap}>
          <View style={styles.slideContainer}>
            {imageUrl ? (
              <Image source={{uri: imageUrl}} style={styles.slideImage} resizeMode="cover" />
            ) : null}
          </View>
        </Pressable>
      );
    },
    [handleSlidePress, isWebDesktopScreen],
  );

  if (isLoading && !slides?.length) {
    return (
      <View
        testID={`${testID}-loading`}
        onLayout={handleLayout}
        style={[styles.loadingContainer, isWebDesktopScreen ? styles.desktopHeight : null]}>
        <ActivityIndicator size="large" color={theme.colour.activityIndicator.primary} />
      </View>
    );
  }

  if (!slides?.length) {
    return null;
  }

  const showArrows = !isSingleSlide && isWebDesktopScreen && showArrowsConfig;

  return (
    <View
      testID={testID}
      onLayout={handleLayout}
      style={isWebDesktopScreen ? styles.outerContainer : undefined}
      // @ts-expect-error - web-only props not in RN types
      onMouseEnter={isSingleSlide ? undefined : handleMouseEnter}
      onMouseLeave={isSingleSlide ? undefined : handleMouseLeave}>
      {containerWidth > 0 && containerHeight > 0 ? (
        <View style={styles.carouselWrapper}>
          <Carousel
            ref={carouselRef}
            data={slides}
            renderItem={renderSlide}
            style={{
              width: carouselWidth,
              height: containerHeight,
              marginHorizontal: -(CAROUSEL_SLIDE_GAP / 2),
            }}
            itemWidth={itemWidth}
            loop={loop}
            overscrollEnabled={false}
            autoPlay={!isSingleSlide && Boolean(autoPlay) && isAutoPlay}
            autoPlayInterval={autoPlayInterval}
            scrollAnimationDuration={transitionDuration}
            onSnapToItem={handleSnapToItem}
            enabled={!isSingleSlide}
          />
          {showArrows && isHovered && (loop || activeIndex > 0) ? (
            <ArrowButton direction="left" onPress={handlePrev} testID={`${testID}-arrow-left`} />
          ) : null}
          {showArrows && isHovered && (loop || activeIndex < slides.length - 1) ? (
            <ArrowButton direction="right" onPress={handleNext} testID={`${testID}-arrow-right`} />
          ) : null}
        </View>
      ) : null}
      {!isSingleSlide && showIndicators ? (
        <Dots count={slides.length} activeIndex={activeIndex} onNext={handleNext} />
      ) : null}
    </View>
  );
};

interface ArrowButtonProps {
  direction: 'left' | 'right';
  onPress: () => void;
  testID?: string;
}

const ArrowButton: React.FC<ArrowButtonProps> = React.memo(({direction, onPress, testID}) => (
  <Pressable
    testID={testID}
    onPress={onPress}
    accessibilityRole="button"
    accessibilityLabel={direction === 'left' ? 'Previous slide' : 'Next slide'}
    style={[styles.arrowButton, direction === 'left' ? styles.arrowLeft : styles.arrowRight]}>
    <Text style={styles.arrowText}>{direction === 'left' ? '\u2039' : '\u203A'}</Text>
  </Pressable>
));

ArrowButton.displayName = 'ArrowButton';

interface DotsProps {
  count: number;
  activeIndex: number;
  onNext?: () => void;
}

const Dots: React.FC<DotsProps> = React.memo(({count, activeIndex, onNext}) => (
  <View style={styles.dotsContainer}>
    {Array.from({length: count}).map((_, index) => (
      <View
        key={index}
        style={[styles.dot, index === activeIndex ? styles.activeDot : styles.inactiveDot]}
      />
    ))}
    {onNext ? (
      <Pressable
        onPress={onNext}
        accessibilityRole="button"
        accessibilityLabel="Next slide"
        style={styles.nextArrow}>
        <Svg width={10} height={10} viewBox="0 0 24 24" fill="none">
          <Path
            d="M9 6l6 6-6 6"
            stroke={theme.colour.background.general.contrastOnPrimary}
            strokeWidth={2.5}
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </Svg>
      </Pressable>
    ) : null}
  </View>
));

Dots.displayName = 'Dots';

export default HomepageCarouselWeb;

const PRIMARY_COLOR = theme.colour.background.general.primary;

const styles = StyleSheet.create({
  outerContainer: {
    width: '100%',
    maxWidth: BANNER_DESKTOP_MAX_WIDTH,
    alignSelf: 'center',
  },
  loadingContainer: {
    width: '100%',
    aspectRatio: BANNER_ASPECT_RATIO_MOBILE,
    borderRadius: CAROUSEL_BORDER_RADIUS,
    backgroundColor: '#f0f0f0',
    justifyContent: 'center',
    alignItems: 'center',
  },
  desktopHeight: {
    maxHeight: BANNER_DESKTOP_MAX_HEIGHT,
    aspectRatio: BANNER_ASPECT_RATIO_WEB,
  },
  carouselWrapper: {
    position: 'relative',
    borderRadius: CAROUSEL_BORDER_RADIUS,
    overflow: 'hidden',
  },
  slideGap: {
    flex: 1,
    paddingHorizontal: CAROUSEL_SLIDE_GAP / 2,
  },
  slideContainer: {
    flex: 1,
    borderRadius: CAROUSEL_BORDER_RADIUS,
    overflow: 'hidden',
  },
  slideImage: {
    width: '100%',
    height: '100%',
  },
  arrowButton: {
    position: 'absolute',
    top: '50%',
    width: CAROUSEL_ARROW_SIZE,
    height: CAROUSEL_ARROW_SIZE,
    borderRadius: CAROUSEL_ARROW_SIZE / 2,
    backgroundColor: 'rgba(0, 0, 0, 0.45)',
    justifyContent: 'center',
    alignItems: 'center',
    transform: [{translateY: -(CAROUSEL_ARROW_SIZE / 2)}],
  },
  arrowLeft: {
    left: 18,
  },
  arrowRight: {
    right: 18,
  },
  arrowText: {
    fontSize: 20,
    lineHeight: 22,
    color: '#ffffff',
    fontWeight: '600',
  },
  dotsContainer: {
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    marginTop: CAROUSEL_DOTS.marginTop,
    marginBottom: CAROUSEL_DOTS.marginBottom,
  },
  dot: {
    width: CAROUSEL_DOTS.dotSize,
    height: CAROUSEL_DOTS.dotSize,
    borderRadius: CAROUSEL_DOTS.dotSize / 2,
    marginHorizontal: CAROUSEL_DOTS.dotMarginHorizontal,
  },
  activeDot: {
    backgroundColor: PRIMARY_COLOR,
  },
  inactiveDot: {
    backgroundColor: 'grey',
    opacity: 0.5,
  },
  nextArrow: {
    marginLeft: 4,
    width: CAROUSEL_DOTS.dotSize * 2.5,
    height: CAROUSEL_DOTS.dotSize * 2.5,
    borderRadius: CAROUSEL_DOTS.dotSize * 1.25,
    backgroundColor: theme.colour.background.general.primary,
    justifyContent: 'center',
    alignItems: 'center',
    cursor: 'pointer',
  },
});
